import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from "react";
import { Box, Container, Text, Flex, Image, Icon, Spinner, Center, } from "@chakra-ui/react";
import { useParams, useNavigate } from "react-router-dom";
import { usePubSectionImages } from "../../hooks/usePubSectionImages";
import { FaExternalLinkAlt } from "react-icons/fa";
// Helper function to check if URL is a video
const isVideo = (url) => {
    if (!url)
        return false;
    const baseUrl = url.split("?")[0];
    return /(0?mp4|0?mov|0?webm|\.mp4|\.mov|\.webm|\.avi)$/i.test(baseUrl);
};
const SectionCard = ({ title, subtitle, items, onClick, onItemClick, sectionType, pubColors, }) => {
    var _a, _b;
    const textColor = (_a = pubColors === null || pubColors === void 0 ? void 0 : pubColors.text) !== null && _a !== void 0 ? _a : "white";
    const cardBg = (_b = pubColors === null || pubColors === void 0 ? void 0 : pubColors.card_bg) !== null && _b !== void 0 ? _b : "#000000";
    // Show first 5 items in the collage (2 in first row, 3 in second row)
    const displayItems = items.slice(0, 5);
    const remainingCount = items.length - 5;
    // Split items into first row (2 items) and second row (3 items)
    const firstRowItems = displayItems.slice(0, 2);
    const secondRowItems = displayItems.slice(2, 5);
    // If no items, show empty state
    if (displayItems.length === 0) {
        return (_jsxs(Box, { cursor: "pointer", onClick: onClick, position: "relative", _hover: { opacity: 0.9 }, transition: "opacity 0.2s", flexShrink: 0, children: [_jsx(Box, { position: "relative", w: "100%", h: { base: "300px", sm: "380px", md: "450px", lg: "480px" }, bg: cardBg, mb: 4, display: "flex", alignItems: "center", justifyContent: "center", children: _jsx(Text, { color: textColor, opacity: 0.5, fontSize: "sm", children: "No media available" }) }), _jsxs(Flex, { align: "center", justify: "space-between", gap: 2, px: 1, children: [_jsxs(Box, { flex: "1", children: [_jsx(Text, { fontSize: { base: "sm", md: "md" }, fontWeight: "700", color: textColor, textTransform: "uppercase", letterSpacing: "0.1em", mb: 1, children: title }), _jsx(Text, { fontSize: { base: "xs", md: "sm" }, color: textColor, opacity: 0.8, children: subtitle })] }), _jsx(Icon, { as: FaExternalLinkAlt, boxSize: 4, color: textColor, flexShrink: 0 })] })] }));
    }
    return (_jsxs(Box, { cursor: "pointer", onClick: onClick, position: "relative", _hover: { opacity: 0.9 }, transition: "opacity 0.2s", flexShrink: 0, pb: 4, children: [_jsx(Box, { position: "relative", w: "100%", h: { base: "300px", sm: "380px", md: "450px", lg: "480px" }, display: "flex", flexDirection: "column", overflow: "hidden", mb: 4, border: "1px solid #A1A1A1", children: displayItems.length === 1 ? (_jsx(Box, { position: "relative", overflow: "hidden", bg: cardBg, w: "100%", h: "100%", cursor: "pointer", onClick: (e) => {
                        e.stopPropagation();
                        if (onItemClick) {
                            onItemClick(displayItems[0], 0, sectionType);
                        }
                    }, _hover: { opacity: 0.9 }, transition: "opacity 0.2s", children: isVideo(displayItems[0]) ? (_jsx(Box, { as: "video", src: displayItems[0], w: "100%", h: "100%", objectFit: "cover", autoPlay: true, loop: true, muted: true, playsInline: true, preload: "auto" })) : (_jsx(Image, { src: displayItems[0], alt: "", w: "100%", h: "100%", objectFit: "cover" })) })) : (_jsxs(_Fragment, { children: [firstRowItems.length > 0 && (_jsxs(Flex, { h: secondRowItems.length === 0 ? "100%" : "60%", children: [firstRowItems.map((item, index) => (_jsx(Box, { position: "relative", overflow: "hidden", bg: cardBg, flex: "1", h: "100%", cursor: "pointer", onClick: (e) => {
                                        e.stopPropagation();
                                        if (onItemClick) {
                                            onItemClick(item, index, sectionType);
                                        }
                                    }, _hover: { opacity: 0.9 }, transition: "opacity 0.2s", children: isVideo(item) ? (_jsx(Box, { as: "video", src: item, w: "100%", h: "100%", objectFit: "cover", autoPlay: true, loop: true, muted: true, playsInline: true, preload: "auto" })) : (_jsx(Image, { src: item, alt: "", w: "100%", h: "100%", objectFit: "cover" })) }, index))), firstRowItems.length === 1 && secondRowItems.length === 0 && (_jsx(Box, { flex: "1", bg: cardBg }))] })), secondRowItems.length > 0 && (_jsxs(Flex, { h: "40%", children: [secondRowItems.map((item, index) => {
                                    const isLastItem = index === secondRowItems.length - 1;
                                    return (_jsxs(Box, { position: "relative", overflow: "hidden", bg: cardBg, flex: "1", h: "100%", cursor: "pointer", onClick: (e) => {
                                            e.stopPropagation();
                                            if (onItemClick) {
                                                onItemClick(item, index + firstRowItems.length, sectionType);
                                            }
                                        }, _hover: { opacity: 0.9 }, transition: "opacity 0.2s", children: [isVideo(item) ? (_jsx(Box, { as: "video", src: item, w: "100%", h: "100%", objectFit: "cover", autoPlay: true, loop: true, muted: true, playsInline: true, preload: "auto" })) : (_jsx(Image, { src: item, alt: `${title} - ${index + firstRowItems.length + 1}`, w: "100%", h: "100%", objectFit: "cover" })), isLastItem && remainingCount > 0 && (_jsx(Box, { position: "absolute", top: 0, left: 0, right: 0, bottom: 0, bg: "#000000a6", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1, children: _jsxs(Box, { color: "white", fontSize: "sm", fontWeight: "400", children: ["+", remainingCount] }) }))] }, index + firstRowItems.length));
                                }), Array.from({ length: 3 - secondRowItems.length }).map((_, index) => (_jsx(Box, { flex: "1", bg: cardBg }, `empty-${index}`)))] }))] })) }), _jsxs(Flex, { align: "center", justify: "space-between", gap: 2, pr: 1, children: [_jsxs(Box, { flex: "1", children: [_jsx(Text, { fontSize: { base: "md", md: "md" }, fontWeight: "400", color: textColor, textTransform: "uppercase", letterSpacing: "0.05em", mb: 1, children: title }), _jsx(Text, { fontSize: { base: "10px", md: "xs" }, color: textColor, opacity: 0.8, children: subtitle })] }), _jsx(Icon, { as: FaExternalLinkAlt, boxSize: 4, color: textColor, flexShrink: 0 })] })] }));
};
const DEFAULT_COLORS = {
    primary: "#8155FE",
    text: "white",
    bg: "#111111",
};
const AmbienceVibeSection = ({ pubDetail, pubColors, appContext = "customer", }) => {
    var _a, _b, _c, _d, _e, _f;
    const { pubId } = useParams();
    const navigate = useNavigate();
    const colors = {
        primary: (_a = pubColors === null || pubColors === void 0 ? void 0 : pubColors.primary) !== null && _a !== void 0 ? _a : DEFAULT_COLORS.primary,
        text: (_b = pubColors === null || pubColors === void 0 ? void 0 : pubColors.text) !== null && _b !== void 0 ? _b : DEFAULT_COLORS.text,
        bg: (_c = pubColors === null || pubColors === void 0 ? void 0 : pubColors.bg) !== null && _c !== void 0 ? _c : DEFAULT_COLORS.bg,
    };
    const { data: sectionImagesData, isLoading: isLoadingSections } = usePubSectionImages(pubId);
    // Get data from pubDetail prop
    const vibes = (_d = pubDetail === null || pubDetail === void 0 ? void 0 : pubDetail.vibes) !== null && _d !== void 0 ? _d : [];
    const pubImages = (_e = pubDetail === null || pubDetail === void 0 ? void 0 : pubDetail.pub_images) !== null && _e !== void 0 ? _e : [];
    const sectionAreas = (_f = sectionImagesData === null || sectionImagesData === void 0 ? void 0 : sectionImagesData.data) !== null && _f !== void 0 ? _f : [];
    // Process vibes: sort videos first
    const sortedVibes = useMemo(() => {
        if (!vibes.length)
            return [];
        const videos = vibes.filter((url) => isVideo(url));
        const images = vibes.filter((url) => !isVideo(url));
        return [...videos, ...images];
    }, [vibes]);
    // Combine all table area images into one array and sort videos first
    const tableAreaImages = useMemo(() => {
        if (!sectionAreas.length)
            return [];
        const allImages = sectionAreas.flatMap((area) => area.images || []);
        const videos = allImages.filter((url) => isVideo(url));
        const images = allImages.filter((url) => !isVideo(url));
        return [...videos, ...images];
    }, [sectionAreas]);
    // Sort venue images: videos first
    const sortedPubImages = useMemo(() => {
        if (!pubImages.length)
            return [];
        const videos = pubImages.filter((url) => isVideo(url));
        const images = pubImages.filter((url) => !isVideo(url));
        return [...videos, ...images];
    }, [pubImages]);
    // Determine which sections to show
    const hasChilloutMoments = sortedVibes.length > 0;
    const hasTableAreas = tableAreaImages.length > 0;
    const hasVenueImages = sortedPubImages.length > 0;
    const sectionsCount = (hasChilloutMoments ? 1 : 0) +
        (hasTableAreas ? 1 : 0) +
        (hasVenueImages ? 1 : 0);
    // Navigation handlers
    const handleChilloutMomentsClick = () => {
        if (appContext === "experience") {
            navigate(`/gallery?section=chillout`);
        }
        else {
            navigate(`/pub/detail/gallery/${pubId}?section=chillout`);
        }
    };
    const handleTableAreasClick = () => {
        if (appContext === "experience") {
            navigate(`/gallery?section=table`);
        }
        else {
            navigate(`/pub/detail/gallery/${pubId}?section=table`);
        }
    };
    const handleVenueImagesClick = () => {
        if (appContext === "experience") {
            navigate(`/gallery?section=venue`);
        }
        else {
            navigate(`/pub/detail/gallery/${pubId}?section=venue`);
        }
    };
    // Handle individual item click - navigate to gallery with specific item
    const handleItemClick = (itemUrl, itemIndex, sectionType) => {
        const encodedUrl = encodeURIComponent(itemUrl);
        if (appContext === "experience") {
            navigate(`/gallery?section=${sectionType}&url=${encodedUrl}`);
        }
        else if (pubId) {
            navigate(`/pub/detail/gallery/${pubId}?section=${sectionType}&url=${encodedUrl}`);
        }
    };
    // Don't render if loading
    if (isLoadingSections) {
        return (_jsx(Box, { w: "100%", py: 8, bg: colors.bg, children: _jsx(Center, { children: _jsx(Spinner, { size: "xl", color: colors.primary }) }) }));
    }
    // Don't render if no sections have data
    if (sectionsCount === 0) {
        return null;
    }
    return (_jsxs(Box, { w: "100%", position: "relative", py: [8, 8, 8, 16], children: [_jsx(Container, { maxW: "container.xl", px: { base: 5, md: 8 }, children: _jsx(Box, { mb: 8, children: _jsxs(Text, { fontSize: ["2xl", "2xl", "3xl", "4xl"], color: colors.text, mb: 3, textTransform: "uppercase", lineHeight: "1.5", textAlign: { base: "left", md: "left" }, children: ["CATCH A GLIMPSE OF OUR ", _jsx("br", {}), " AMBIENCE AND VIBE."] }) }) }), _jsx(Box, { w: "100%", overflowX: { base: "auto", sm: "auto", md: "hidden" }, overflowY: "hidden", css: {
                    "&::-webkit-scrollbar": {
                        height: "4px",
                    },
                    "&::-webkit-scrollbar-track": {
                        background: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        background: colors.primary,
                    },
                }, children: _jsx(Container, { maxW: "container.xl", px: { base: 5, md: 8 }, children: _jsxs(Flex, { gap: { base: 4, sm: 5, md: 4, lg: 6 }, w: { base: "auto", sm: "auto", md: "100%" }, pr: { base: 6, sm: 6, md: 4, lg: 0 }, justifyContent: {
                            base: "flex-start",
                            sm: "flex-start",
                            md: "center",
                        }, maxW: "100%", children: [hasChilloutMoments && (_jsx(Box, { w: {
                                    base: sectionsCount === 1 ? "100%" : "270px",
                                    sm: "320px",
                                    md: "300px",
                                    lg: "350px",
                                    xl: "400px",
                                }, flexShrink: 0, children: _jsx(SectionCard, { title: "CHILLOUT MOMENTS", subtitle: "Where Every Moment Feels Right", items: sortedVibes, onClick: handleChilloutMomentsClick, onItemClick: handleItemClick, sectionType: "chillout", pubColors: pubColors }) })), hasTableAreas && (_jsx(Box, { w: {
                                    base: sectionsCount === 1 ? "100%" : "270px",
                                    sm: "320px",
                                    md: "300px",
                                    lg: "350px",
                                    xl: "400px",
                                }, flexShrink: 0, children: _jsx(SectionCard, { title: "EXPLORE TABLE AREAS", subtitle: "Find Your Perfect Spot", items: tableAreaImages, onClick: handleTableAreasClick, onItemClick: handleItemClick, sectionType: "table", pubColors: pubColors }) })), hasVenueImages && (_jsx(Box, { w: {
                                    base: sectionsCount === 1 ? "100%" : "270px",
                                    sm: "320px",
                                    md: "300px",
                                    lg: "350px",
                                    xl: "400px",
                                }, flexShrink: 0, children: _jsx(SectionCard, { title: "VENUE IMAGES", subtitle: "Have a sneak peek into our ambience", items: sortedPubImages, onClick: handleVenueImagesClick, onItemClick: handleItemClick, sectionType: "venue", pubColors: pubColors }) }))] }) }) })] }));
};
export default AmbienceVibeSection;
